import React, { useState } from 'react';
import { GroupMemberProfile } from '../types';
import { X, ArrowRightLeft, Trophy, Goal, Award, Shield, Percent } from 'lucide-react';

interface PlayerComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  members: GroupMemberProfile[];
  initialPlayerAId?: string;
  initialPlayerBId?: string;
}

export const PlayerComparisonModal: React.FC<PlayerComparisonModalProps> = ({
  isOpen,
  onClose,
  members,
  initialPlayerAId,
  initialPlayerBId,
}) => {
  if (!isOpen) return null;

  const defaultPlayerA = members.find((m) => m.id === initialPlayerAId) || members[0];
  const defaultPlayerB = members.find((m) => m.id === initialPlayerBId) || members[1] || members[0];

  const [playerAId, setPlayerAId] = useState<string>(defaultPlayerA?.id || '');
  const [playerBId, setPlayerBId] = useState<string>(defaultPlayerB?.id || '');

  const playerA = members.find((m) => m.id === playerAId) || members[0];
  const playerB = members.find((m) => m.id === playerBId) || members[1] || members[0];

  if (!playerA || !playerB) return null;

  const compareAttrs = [
    { label: 'Pace (PAC)', valA: playerA.footballCard.pace, valB: playerB.footballCard.pace },
    { label: 'Shooting (SHO)', valA: playerA.footballCard.shooting, valB: playerB.footballCard.shooting },
    { label: 'Passing (PAS)', valA: playerA.footballCard.passing, valB: playerB.footballCard.passing },
    { label: 'Dribbling (DRI)', valA: playerA.footballCard.dribbling, valB: playerB.footballCard.dribbling },
    { label: 'Defending (DEF)', valA: playerA.footballCard.defending, valB: playerB.footballCard.defending },
    { label: 'Physical (PHY)', valA: playerA.footballCard.physical, valB: playerB.footballCard.physical },
  ];

  const compareStats = [
    { label: 'Matches Played', valA: playerA.stats.matchesPlayed, valB: playerB.stats.matchesPlayed },
    { label: 'Goals', valA: playerA.stats.goals, valB: playerB.stats.goals },
    { label: 'Assists', valA: playerA.stats.assists, valB: playerB.stats.assists },
    { label: 'Average Rating', valA: playerA.stats.averageRating.toFixed(1), valB: playerB.stats.averageRating.toFixed(1) },
    { label: 'Win %', valA: `${playerA.stats.winPct}%`, valB: `${playerB.stats.winPct}%` },
    { label: 'MOTM Awards', valA: playerA.stats.motmCount, valB: playerB.stats.motmCount },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#121614] border border-white/10 rounded-2xl w-full max-w-2xl p-5 sm:p-6 shadow-2xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex justify-between items-center mb-5">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-[#a8c936]" />
            <h3 className="font-bold text-lg text-white">Player Comparison</h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Player Pickers Header */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {/* Player A Picker */}
          <div className="bg-[#171c19] p-3 rounded-xl border border-white/5 flex flex-col items-center text-center">
            <img src={playerA.avatar} alt={playerA.displayName} className="w-14 h-14 rounded-xl object-cover border border-white/10 mb-2" />
            <select
              value={playerA.id}
              onChange={(e) => setPlayerAId(e.target.value)}
              className="bg-[#080b0a] text-white border border-white/10 text-xs font-bold rounded-lg px-2 py-1 max-w-full focus:outline-none"
            >
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.primaryPosition})
                </option>
              ))}
            </select>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xl font-extrabold text-white font-mono">{playerA.footballCard?.overall ?? 0}</span>
              <span className="text-[10px] font-semibold text-[#a8c936] bg-[#a8c936]/10 px-1.5 py-0.5 rounded border border-[#a8c936]/20">
                {playerA.primaryPosition}
              </span>
            </div>
          </div>

          {/* Player B Picker */}
          <div className="bg-[#171c19] p-3 rounded-xl border border-white/5 flex flex-col items-center text-center">
            <img src={playerB.avatar} alt={playerB.displayName} className="w-14 h-14 rounded-xl object-cover border border-white/10 mb-2" />
            <select
              value={playerB.id}
              onChange={(e) => setPlayerBId(e.target.value)}
              className="bg-[#080b0a] text-white border border-white/10 text-xs font-bold rounded-lg px-2 py-1 max-w-full focus:outline-none"
            >
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.primaryPosition})
                </option>
              ))}
            </select>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xl font-extrabold text-white font-mono">{playerB.footballCard?.overall ?? 0}</span>
              <span className="text-[10px] font-semibold text-[#a8c936] bg-[#a8c936]/10 px-1.5 py-0.5 rounded border border-[#a8c936]/20">
                {playerB.primaryPosition}
              </span>
            </div>
          </div>
        </div>

        {/* Attributes Comparison */}
        <div className="space-y-4 mb-6">
          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Football Attributes</h4>
          <div className="space-y-2.5">
            {compareAttrs.map((attr) => {
              const diff = attr.valA - attr.valB;
              return (
                <div key={attr.label} className="bg-[#171c19] p-2.5 rounded-xl border border-white/5 space-y-1">
                  <div className="flex justify-between items-center text-xs font-semibold">
                    <span className={`font-mono ${diff > 0 ? 'text-[#a8c936] font-bold' : 'text-slate-300'}`}>
                      {attr.valA}
                    </span>
                    <span className="text-slate-400 font-medium">{attr.label}</span>
                    <span className={`font-mono ${diff < 0 ? 'text-[#a8c936] font-bold' : 'text-slate-300'}`}>
                      {attr.valB}
                    </span>
                  </div>
                  {/* Visual Comparison Bar */}
                  <div className="flex gap-1 h-1.5 rounded-full overflow-hidden bg-[#080b0a]">
                    <div
                      className="bg-[#a8c936] h-full transition-all"
                      style={{ width: `${(attr.valA / (attr.valA + attr.valB)) * 100}%` }}
                    />
                    <div
                      className="bg-slate-600 h-full transition-all"
                      style={{ width: `${(attr.valB / (attr.valA + attr.valB)) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Career Stats Comparison */}
        <div className="space-y-3">
          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Career Statistics</h4>
          <div className="grid grid-cols-1 gap-2">
            {compareStats.map((stat) => (
              <div key={stat.label} className="bg-[#171c19] p-2.5 rounded-xl border border-white/5 flex items-center justify-between text-xs">
                <span className="font-bold text-white font-mono">{stat.valA}</span>
                <span className="text-slate-400 font-medium">{stat.label}</span>
                <span className="font-bold text-white font-mono">{stat.valB}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
